import { Grid } from '@mui/material'
import { EquipmentType } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { FieldBox, PositionRangeAutocomplete, YardIcon } from '@tom-ui/ui'
import { IFormStore, useRegisterSubFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { IYardBlockAssignment } from '../../models/operational-equipment-assignment.model'
import { YardBlockAssignmentSubFormStore } from '../../stores/yard-block-assignment.sub-form-store'
import { OperationalEquipmentAdd } from '../atoms/OperationalEquipmentAdd'
import { OperationalEquipmentAssignmentActions } from '../atoms/OperationalEquipmentAssignmentActions'
import { OperationalEquipmentDuplicatedAlert } from '../atoms/OperationalEquipmentDuplicatedAlert'
import { OperationalEquipmentSharedAssignment } from './OperationalEquipmentSharedAssignment'
import { YardBlockAreaAutoComplete } from './YardBlockAreaAutoComplete'

interface Props<T> {
  name: keyof T
  formStore: IFormStore<T>
  equipmentType?: EquipmentType
  equipmentCount: number
  initialSelectedAssignments: IYardBlockAssignment[]
}
export const OperationalEquipmentYardAssignmentSubForm = observer(
  <T,>({
    name,
    formStore,
    equipmentCount,
    equipmentType,
    initialSelectedAssignments,
  }: Props<T>) => {
    const { t } = useTranslate()
    const store = useRegisterSubFormStore(formStore, name, new YardBlockAssignmentSubFormStore())

    useEffect(() => {
      store.setValues(initialSelectedAssignments, equipmentCount > 1)
    }, [initialSelectedAssignments, equipmentCount, store])

    return (
      <FieldBox
        label={t('yardAssignment', 'Yard assignment')}
        labelIcon={<YardIcon />}
        rightRender={
          <OperationalEquipmentAdd
            canAdd={store.canAddAssignment}
            onAdd={() => store.addAssignment()}
            dataCy='add-yard-assignment'
          />
        }
        displayChildren={store.displayContent}
      >
        <Grid container spacing={2} alignItems='center'>
          <OperationalEquipmentSharedAssignment
            equipmentCount={equipmentCount}
            equipmentType={equipmentType}
            assignmentsTotal={store.workAreaIds.value.length}
            canAddAssignment={!store.assignments.value.length}
            assignmentType={t('yardBlocks', 'Yard blocks')}
            onAdd={() => store.addAssignment()}
            onRemove={() => store.clearWorkAreas()}
          />
          {store.assignments.value.map(
            (assignment: IYardBlockAssignment | null, index: number) =>
              assignment?.isDeleted !== true && (
                <Grid container size={12} alignItems='center' key={`yard-assignment-${index}`}>
                  <Grid size={5}>
                    <YardBlockAreaAutoComplete
                      label={t('block', 'Block')}
                      dataCy={`yard-assignment-block-${index}`}
                      equipmentType={equipmentType}
                      selectedId={assignment?.workAreaId}
                      initialFilterName={assignment?.filter}
                      onChange={yardBlock => {
                        store.updateAssignment(
                          index,
                          yardBlock?.workAreaId,
                          yardBlock?.bays,
                          [],
                          yardBlock?.name,
                        )
                      }}
                      onLoadBays={bays =>
                        store.updateAssignment(
                          index,
                          assignment?.workAreaId,
                          bays,
                          assignment?.assignedBays,
                          assignment?.blockName,
                        )
                      }
                    />
                  </Grid>

                  <Grid size={5}>
                    <PositionRangeAutocomplete
                      label={t('bays', 'Bays')}
                      value={assignment?.assignedBays}
                      options={assignment?.bays ?? []}
                      handleChange={value => store.updateBays(index, assignment?.bays, value)}
                      dataCy={`yard-assignment-bays-${index}`}
                    />
                  </Grid>

                  <OperationalEquipmentAssignmentActions
                    canAddAssignment={
                      index ===
                      store.assignments.value.reduce(
                        (lastIdx, a, i) => (a && a.isDeleted !== true ? i : lastIdx),
                        -1,
                      )
                    }
                    onAdd={() => store.addAssignment()}
                    onRemove={() => store.removeAssignment(index)}
                  />
                </Grid>
              ),
          )}
          <OperationalEquipmentDuplicatedAlert
            duplicatedAssignments={store.duplicatedAssignments}
            type='blocks'
          />
        </Grid>
      </FieldBox>
    )
  },
)
